import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

export function approxCount(n: number): string {
    if (n < 10) return String(n)
    if (n < 1000) {
        const magnitude = Math.pow(10, Math.floor(Math.log10(n)))
        return `${Math.floor(n / magnitude) * magnitude}+`
    }
    if (n < 1_000_000) {
        const thousands = n / 1000
        const magnitude = Math.pow(10, Math.floor(Math.log10(thousands)))
        return `${Math.floor(thousands / magnitude) * magnitude}k+`
    }
    const millions = n / 1_000_000
    const magnitude = Math.pow(10, Math.floor(Math.log10(millions)))
    return `${Math.floor(millions / magnitude) * magnitude}M+`
}

export interface LeadData {
    first_name?: string
    last_name?: string
    company?: string
    title?: string
    location?: string
}

/**
 * Replace template variables in text with actual lead data.
 *
 * Accepts BOTH the canonical token names from `lib/templateVariables.ts`
 * (`{{normalizedFirstName}}`, `{{normalizedCompanyName}}`) AND the legacy
 * aliases (`{{firstName}}`, `{{company}}`) that older templates and chat
 * outputs still emit. The editor's TextNode transform only pills the
 * canonical names, so new content should use those, but stored templates
 * with aliases must keep substituting at send time.
 *
 * Supports: {{normalizedFirstName}} / {{firstName}}, {{lastName}},
 * {{normalizedCompanyName}} / {{company}}, {{position}}, {{location}},
 * {{senderFirstName}}, {{signature}}.
 */
export function replaceTemplateVariables(text: string, lead: LeadData | null, senderFirstName?: string, senderSignatureHtml?: string): string {
    if (!text) return text

    let result = text

    // Replace lead variables. Each token has a canonical form (left side
    // of the OR) plus a legacy alias (right side); both substitute to the
    // same value so existing templates keep working.
    if (lead) {
        result = result.replace(/\{\{normalizedFirstName\}\}/g, lead.first_name || "{{normalizedFirstName}}")
        result = result.replace(/\{\{firstName\}\}/g, lead.first_name || "{{firstName}}")
        result = result.replace(/\{\{lastName\}\}/g, lead.last_name || "{{lastName}}")
        result = result.replace(/\{\{normalizedCompanyName\}\}/g, lead.company || "{{normalizedCompanyName}}")
        result = result.replace(/\{\{company\}\}/g, lead.company || "{{company}}")
        result = result.replace(/\{\{position\}\}/g, lead.title || "{{position}}")
        result = result.replace(/\{\{location\}\}/g, lead.location || "{{location}}")
    }

    // Replace sender variables
    if (senderFirstName) {
        result = result.replace(/\{\{senderFirstName\}\}/g, senderFirstName)
    }

    // Signature substitutes inline as raw HTML. Callers that resolve a
    // {{signature}} token should also suppress their auto-append step so the
    // signature isn't duplicated at the bottom of the message.
    result = result.replace(/\{\{signature\}\}/g, senderSignatureHtml || "")

    return result
}

/** Detects whether the body already contains a `{{signature}}` token — used by
 *  the send pipeline to decide whether to auto-append the account signature
 *  (skip if the user placed it inline). */
export function bodyContainsSignatureToken(text: string | null | undefined): boolean {
    if (!text) return false
    return /\{\{signature\}\}/.test(text)
}
